import { db } from './db.js';
import { baseRecord, createId } from './id.js';
/**
 * Attachments stay on the device. The cap is deliberately conservative so an
 * export can still be recovered on a phone without silently truncating media.
 */
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
export const MAX_BACKUP_ATTACHMENT_BYTES = 20 * 1024 * 1024;
export class AttachmentTooLargeError extends Error {
    code = 'BACKUP_TOO_LARGE';
    constructor() {
        super('첨부파일은 20MB까지 보관할 수 있어요.');
        this.name = 'AttachmentTooLargeError';
    }
}
export function assertBackupAttachmentSize(rows) {
    const total = rows.reduce((sum, row) => sum + row.blob.size, 0);
    if (total > MAX_BACKUP_ATTACHMENT_BYTES)
        throw new AttachmentTooLargeError();
}
export async function putAttachment(input) {
    if (input.blob.size > MAX_ATTACHMENT_BYTES)
        throw new AttachmentTooLargeError();
    const now = Date.now();
    const row = {
        ...baseRecord({
            petId: input.petId,
            blob: input.blob,
            mime: input.blob.type || 'application/octet-stream',
            ...(input.width == null ? {} : { width: input.width }),
            ...(input.height == null ? {} : { height: input.height }),
        }),
        ...(input.id ? { id: input.id } : {}),
        createdAt: now,
        updatedAt: now,
    };
    await db.attachments.put(row);
    return row;
}
export async function getAttachment(id) {
    return db.attachments.get(id);
}
export async function listAttachments(petId) {
    return db.attachments.where('petId').equals(petId).filter((row) => !row.deletedAt).toArray();
}
export async function deleteAttachment(id) {
    await db.attachments.delete(id);
}
export async function attachmentToBackup(row) {
    return {
        id: row.id,
        petId: row.petId,
        mime: row.mime,
        ...(row.width == null ? {} : { width: row.width }),
        ...(row.height == null ? {} : { height: row.height }),
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
        ...(row.deletedAt == null ? {} : { deletedAt: row.deletedAt }),
        dataUrl: await blobToDataUrl(row.blob),
    };
}
export async function attachmentFromBackup(row) {
    const blob = dataUrlToBlob(row.dataUrl, row.mime);
    if (blob.size > MAX_ATTACHMENT_BYTES)
        throw new AttachmentTooLargeError();
    return {
        id: row.id || createId(),
        petId: row.petId,
        blob,
        mime: row.mime,
        ...(row.width == null ? {} : { width: row.width }),
        ...(row.height == null ? {} : { height: row.height }),
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
        ...(row.deletedAt == null ? {} : { deletedAt: row.deletedAt }),
    };
}
async function blobToDataUrl(blob) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let binary = '';
    const chunkSize = 0x8000;
    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
    }
    const base64 = btoa(binary);
    return `data:${blob.type || 'application/octet-stream'};base64,${base64}`;
}
function dataUrlToBlob(dataUrl, mime) {
    const comma = dataUrl.indexOf(',');
    if (comma < 0)
        throw new Error('첨부파일 형식을 읽지 못했어요.');
    const encoded = dataUrl.slice(comma + 1);
    const binary = atob(encoded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new Blob([bytes], { type: mime || 'application/octet-stream' });
}
